import React, { useEffect, useState } from 'react'
import StyledActionsDialog from 'components/atoms/StyledActionsDialog'
import StyledModalDialog from 'components/atoms/StyledModalDialog'
import Transition from 'components/atoms/Transition'
import { Button, Loader, Checkbox, Search, useInput } from 'sputnik-ui'
import styled, { css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import FoldButton from 'components/atoms/FoldButton'
import { getStreets, getHousesPromise } from 'store/operations'
import {
  DialogContent,
  DialogTitle,
  Typography,
  FormControlLabel,
  Switch,
  DialogContentText,
  duration,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { unwrapResult } from '@reduxjs/toolkit'

const ListWrapper = styled.div`
  max-height: 26rem;
  padding: 16px;
  overflow-y: scroll;
  &:last-child {
    padding-bottom: 0;
  }
  &::-webkit-scrollbar {
    width: 3px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgb(192, 192, 192);
  }
`

const SecondaryListWrapper = styled.div`
  max-height: 0rem;
  padding: 0;
  overflow-y: scroll;
  &:last-child {
    padding-bottom: 0;
  }
  &::-webkit-scrollbar {
    width: 3px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgb(192, 192, 192);
  }

  ${(p) =>
    p.isExpanded &&
    css`
      padding-left: 20px;
      max-height: 10rem !important;
    `}
`

const ChooseEmergencyAddresses = ({
  open,
  handleClose,
  handleAddressChange,
  choosenAddresses,
  selectAll,
  onSuccess,
  ...props
}) => {
  const { t } = useTranslation(['common', 'emergency'])
  const dispatch = useDispatch()
  const { streets } = useSelector((state) => ({
    streets: state.resources.streets.list,
  }))
  const [debouncedQuery, setDebouncedQuery] = useState()
  const [expanded, setExpanded] = useState({})
  const [houses, setHouses] = useState({})

  useEffect(() => {
    if (open) {
      const params = {
        q: debouncedQuery,
      }
      if (!params.q) {
        delete params.q
      }
      dispatch(getStreets(params))
    }
  }, [open, dispatch, debouncedQuery])

  const { value: query, bind } = useInput()

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)
    return () => clearTimeout(handler)
  }, [query])

  const loadHouses = async (streetUuid) => {
    const response = await dispatch(
      getHousesPromise({ parentUuid: streetUuid })
    )
    return response
  }

  const toggleExpand = async (streetUuid) => {
    setExpanded((prev) => ({
      ...prev,
      [streetUuid]: !prev[streetUuid],
    }))

    if (!houses[streetUuid]) {
      const data = await loadHouses(streetUuid)
      setHouses((prev) => ({
        ...prev,
        [streetUuid]: data,
      }))
    }
  }

  return (
    <StyledModalDialog
      open={open}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      onClose={handleClose}
      PaperProps={{
        style: { borderRadius: 20, maxHeight: '100vh' },
      }}
      {...props}
    >
      <DialogTitle>{t('common:choose.addresses')}</DialogTitle>
      <Search {...bind} placeholder={`${t('common:search.addresses')}...`} />
      <ListWrapper>
        <Checkbox
          onClick={() =>
            selectAll(
              Object.fromEntries(streets.map((street) => [street.uuid, []]))
            )
          }
          checked={choosenAddresses?.length === streets?.length}
          color="blue"
          style={{ margin: 0, padding: 0, marginRight: 16 }}
        />
        <span>{t('common:button.choose_all')}</span>
        <List>
          {streets ? (
            streets.map(({ full_name, uuid }) => (
              <React.Fragment key={uuid}>
                <ListItem>
                  <Checkbox
                    color="blue"
                    checked={!!choosenAddresses[uuid]}
                    onClick={() => handleAddressChange(uuid, null)}
                    style={{ margin: 0, padding: 0, marginRight: 16 }}
                  />
                  <ListItemText primary={full_name} />
                  <ListItemSecondaryAction>
                    <FoldButton
                      disableRipple
                      isExpanded={!!expanded[uuid]}
                      onClick={() => toggleExpand(uuid)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <SecondaryListWrapper isExpanded={expanded[uuid]}>
                  {houses[uuid] ? (
                    <List dense>
                      {houses[uuid].map((house) => (
                        <ListItem key={house.uuid}>
                          <Checkbox
                            color="blue"
                            style={{ margin: 0, padding: 0, marginRight: 16 }}
                            checked={
                              !!choosenAddresses[uuid]?.includes(house.uuid)
                            }
                            onClick={() =>
                              handleAddressChange(uuid, house.uuid)
                            }
                          />
                          <ListItemText primary={house.full_name} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    expanded[uuid] && <Loader fluid />
                  )}
                </SecondaryListWrapper>
              </React.Fragment>
            ))
          ) : (
            <Loader fluid />
          )}
        </List>
      </ListWrapper>
      <StyledActionsDialog>
        <Button
          type="submit"
          primary
          variant="v2"
          fullWidth
          style={{ marginBottom: '1rem', width: '20rem' }}
          onClick={onSuccess}
        >
          {t('common:button.confirm_capital')}
        </Button>
        <Button onClick={handleClose} tertiary>
          {t('common:button.cancel')}
        </Button>
      </StyledActionsDialog>
    </StyledModalDialog>
  )
}

export default ChooseEmergencyAddresses
